import {
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListFieldsRecordType } from "src/api/config";
import { emailQueueListName } from "src/api/config/email.config";
import {
  getNotificationsListName,
  notificationStatusesListName,
  notificationsListName,
  sendNotificationsListName,
} from "src/api/config/notifications.config";
import { notificationStatuses } from "src/api/config/systemSwitch";
import { DrawerComponent, Modal } from "src/components/DialogComponent";
import {
  GetNotificationDrawerMobile,
  TableBodyGetNotificationItem,
} from "src/components/tables/notificationTables/GetNotificationTable.mobile";
import {
  SendNotificationDrawer,
  TableBodySendNotificationItem,
} from "src/components/tables/notificationTables/SendNotificationTable";
import { EmptyList } from "src/components/EmptyList";
import Loading from "src/components/Loading";
import TGButton from "src/components/ui/TGButton";
import { NotificationBigIcon } from "src/components/ui-icons/sidebar/NotificationIcon";
import CommentIcon from "src/components/ui-icons/table/CommentIcon";
import {
  ApproveIcon,
  EyeIcon,
  StarIcon,
} from "src/components/ui-icons/table/notificationStatus";
import { UserAvatarItem } from "src/components/ui/TGUserAvatarsList";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getNotificationsInfo } from "src/modules/acadia/notifications/actions";
import { addAnalysis } from "src/modules/acadia/constants";
import {
  getTransformDataByActiveNotify,
  sendTransformDataByActiveNotify,
} from "src/modules/acadia/notifications/sagas/notificationsInfoWatcher";
import { getItems } from "src/modules/spItems/actions";
import {
  deleteItemRequest,
  getItemsRequest,
  updateItemRequest,
} from "src/modules/spItems/api";
import { selectCount } from "src/modules/spItems/selectors/selectCount";
import { selectJustUpdatedItem } from "src/modules/spItems/selectors";
import { selectCurrentUser, selectUsers } from "src/modules/users/selector";
import { useGetQuestionsCount } from "src/pages/DashboardPage/acadia/components/PassingTest/useGetQuestionsCount";
import { chunkArray } from "src/utils/array.utils";
import { emailAPI } from "src/utils/api.utils";
import { TooltipItem } from "src/utils/Tooltip";
import { shortNameFromEmail } from "src/utils/users.utils";
import ActionCardMobile from "../../throughcomponents/actionCard/actionCard.mobile";

const { deleteEmail } = emailAPI();

type EmployeeItem =
  TableBodySendNotificationItem["Employees"] extends Map<string, infer T>
    ? T
    : never;
type EmployeeEntry = [string, EmployeeItem];

function getNotificationIcon(notificationTypeId: number) {
  if (
    notificationTypeId === notificationStatuses.acquainted ||
    notificationTypeId === notificationStatuses.acquaintedTest ||
    notificationTypeId === notificationStatuses.familiarization
  ) {
    return <EyeIcon />;
  }

  if (notificationTypeId === notificationStatuses.approval) {
    return <ApproveIcon />;
  }

  if (
    notificationTypeId === notificationStatuses.getOWD ||
    notificationTypeId === notificationStatuses.OWD
  ) {
    return <StarIcon />;
  }

  return null;
}

const NotificationPageMobile = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const users = useAppSelector((state) => selectUsers(state));
  const [query, setQuery] = useSearchParams();
  const updateNotification = useAppSelector((state) =>
    selectJustUpdatedItem(state, notificationsListName),
  );

  const [showGet, setShowGet] = useState(true);
  const [showSend, setShowSend] = useState(false);

  const getNotificationsRaw = useAppSelector(
    (state) => state.spitems.elements.obj.GetNotifications,
  ) as unknown as Record<number, TableBodyGetNotificationItem>;

  const sendNotificationsRaw = useAppSelector(
    (state) => state.spitems.elements.obj.SendNotifications,
  ) as unknown as Record<number, TableBodySendNotificationItem>;

  const getNotificationsCount = useAppSelector((state) =>
    selectCount(state, getNotificationsListName),
  );
  const sendNotificationsCount = useAppSelector((state) =>
    selectCount(state, sendNotificationsListName),
  );

  const getNotifications = useMemo(() => {
    if (!getNotificationsRaw) return [];
    return getTransformDataByActiveNotify(
      Object.values(getNotificationsRaw) as any,
    );
  }, [getNotificationsRaw]);

  const sendNotifications = useMemo(() => {
    if (!sendNotificationsRaw) return [];
    return sendTransformDataByActiveNotify(
      Object.values(sendNotificationsRaw) as any,
    );
  }, [sendNotificationsRaw]);

  const notifyCardQuery = useMemo(
    () => query.has("notificationCard"),
    [query],
  );

  useEffect(() => {
    dispatch(getItems({ listName: notificationStatusesListName }));
  }, [dispatch]);

  useEffect(() => {
    if (!currentUser.email) return;

    dispatch(
      getNotificationsInfo({
        activeNotifyList: "get",
        listName: notificationsListName,
      }),
    );

    dispatch(
      getNotificationsInfo({
        activeNotifyList: "send",
        listName: notificationsListName,
      }),
    );
  }, [dispatch, currentUser.email, notifyCardQuery, updateNotification]);

  useEffect(() => {
    if (!query.has("notificationCard") && query.has("notificationRole")) {
      const updatedQuery = new URLSearchParams(query);
      updatedQuery.delete("notificationRole");
      setQuery(updatedQuery);
    }
  }, [query, setQuery]);

  function openGetNotification(item: TableBodyGetNotificationItem) {
    const updatedQuery = new URLSearchParams(query);

    if (
      item.NotificationType.ID === notificationStatuses.approval ||
      item.NotificationType.ID === notificationStatuses.cancel ||
      item.NotificationType.ID === notificationStatuses.approved ||
      item.NotificationType.ID === notificationStatuses.revision
    ) {
      updatedQuery.set("notificationID", item.ID.toString());

      if (item.VersionDocument) {
        updatedQuery.set(
          "version",
          item.VersionDocument.get_lookupId().toString(),
        );
      }

      updatedQuery.set("documentCardMobile", item.Document.ID.toString());
      updatedQuery.delete("notificationCard");
      updatedQuery.delete("notificationRole");
    } else {
      updatedQuery.set("notificationCard", item.ID.toString());
      updatedQuery.set("notificationRole", "get");
    }

    setQuery(updatedQuery);
  }

  function openSendNotification(item: TableBodySendNotificationItem) {
    const updatedQuery = new URLSearchParams(query);
    updatedQuery.set("notificationCard", item.NotificationID.toString());
    updatedQuery.set("notificationRole", "send");
    setQuery(updatedQuery);
  }

  function closeNotificationCard() {
    const updatedQuery = new URLSearchParams(query);
    updatedQuery.delete("notificationCard");
    updatedQuery.delete("notificationRole");
    setQuery(updatedQuery);
  }

  function closeActionCardMobile() {
    const updatedQuery = new URLSearchParams(query);
    updatedQuery.delete("actionCardMobile");
    setQuery(updatedQuery);
  }

  const notificationCardId = Number(query.get("notificationCard"));

  const currentSendNotification = useMemo(() => {
    if (!notificationCardId) return null;

    return (
      sendNotifications.find(
        (item) =>
          Number(item.NotificationID) === notificationCardId ||
          Number(item.ID) === notificationCardId,
      ) ?? null
    );
  }, [notificationCardId, sendNotifications]);

  const shouldOpenMobileSendCard = useMemo(() => {
    if (!query.has("notificationCard")) return false;
    if (query.get("notificationRole") === "get") return false;

    return !!currentSendNotification;
  }, [query, currentSendNotification]);

  return (
    <div className="px-4 pb-5">
      <section>
        <button
          type="button"
          onClick={() => setShowGet((prev) => !prev)}
          className="w-full flex items-center justify-between py-4 text-darkblue font-medium"
        >
          <span>Полученные уведомления · {getNotificationsCount}</span>
          {showGet ? (
            <ChevronUpIcon className="w-5 h-5" />
          ) : (
            <ChevronDownIcon className="w-5 h-5" />
          )}
        </button>
      </section>

      {showGet && (
        <div className="pt-3 space-y-3">
          {getNotifications.length > 0 ? (
            getNotifications.map((item) =>
              (() => {
                const authorEmail = item.Author.User.Email.toLowerCase().trim();
                const authorDisplayName =
                  users[authorEmail]?.displayName || item.Author.User.Name;

                return (
                  <button
                    key={item.ID}
                    type="button"
                    onClick={() => openGetNotification(item)}
                    className="w-full text-left bg-white border border-[#E5E7EB] rounded-xl p-3"
                  >
                    <div className="flex items-center gap-2 text-[#111827] font-medium">
                      {getNotificationIcon(item.NotificationType.ID)}
                      <span>{item.NotificationType.Title}</span>
                    </div>
                    <div className="text-[#637381] mt-1">
                      {item.Document.Code}
                    </div>
                    <div className="text-darkblue leading-5">
                      {item.Document.Name}
                    </div>
                    <div className="text-[#637381] mt-1">
                      Отправлено: {item.SendDate}
                    </div>
                    <div className="mt-1 flex items-start gap-2 text-[#637381]">
                      <span>Кем:</span>
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <UserAvatarItem
                          item={{
                            name: item.Author.User.Name,
                            shortName: shortNameFromEmail(item.Author.User.Email),
                            email: item.Author.User.Email,
                            id: item.Author.User.ID,
                          }}
                        />
                        <span className="min-w-0 break-words whitespace-normal leading-6">
                          {authorDisplayName}
                        </span>
                      </div>
                      {!!item.Author.Comment && (
                        <TooltipItem
                          tooltipsText={item.Author.Comment}
                          position="top"
                          className="w-64"
                          textClassName="whitespace-wrap text-center text-black"
                        >
                          <CommentIcon />
                        </TooltipItem>
                      )}
                    </div>
                    <div
                      className={
                        item.ReviewDate.expired
                          ? "text-red mt-1"
                          : "text-[#637381] mt-1"
                      }
                    >
                      Срок рассмотрения: {item.ReviewDate.date}
                    </div>
                  </button>
                );
              })(),
            )
          ) : (
            <EmptyList
              icon={<NotificationBigIcon />}
              title="У вас нет новых уведомлений"
            />
          )}
        </div>
      )}

      <section className="mt-2">
        <button
          type="button"
          onClick={() => setShowSend((prev) => !prev)}
          className="w-full flex items-center justify-between py-4 text-[#637381] font-medium"
        >
          <span>Отправленные уведомления · {sendNotificationsCount}</span>
          {showSend ? (
            <ChevronUpIcon className="w-5 h-5" />
          ) : (
            <ChevronDownIcon className="w-5 h-5" />
          )}
        </button>
      </section>

      {showSend && (
        <div className="pt-3 space-y-3">
          {sendNotifications.length > 0 ? (
            sendNotifications.map((item) => (
              <button
                key={item.ID}
                type="button"
                onClick={() => openSendNotification(item)}
                className="w-full text-left bg-white border border-[#E5E7EB] rounded-xl p-3"
              >
                <div className="flex items-center gap-2 text-[#111827] font-medium">
                  {getNotificationIcon(item.NotificationType.ID)}
                  <span>{item.NotificationType.Title}</span>
                </div>
                <div className="text-[#637381] mt-1">
                  Отправлено: {item.SendDate}
                </div>
                <div
                  className={
                    item.ReviewDate.expired
                      ? "text-red mt-1"
                      : "text-[#637381] mt-1"
                  }
                >
                  Срок рассмотрения: {item.ReviewDate.date}
                </div>
              </button>
            ))
          ) : (
            <EmptyList
              icon={<NotificationBigIcon />}
              title="У вас нет актуальных отправленных уведомлений"
            />
          )}
        </div>
      )}

      {query.get("notificationRole") === "get" &&
        query.has("notificationCard") && (
          <GetNotificationDrawerMobile
            listName={getNotificationsListName}
            zIndex={90}
          />
        )}
      {shouldOpenMobileSendCard && currentSendNotification && (
        <DrawerComponent
          open={true}
          toggleDrawer={() => closeNotificationCard()}
          zIndex={90}
        >
          <SendNotificationCardMobile
            item={currentSendNotification}
            onClose={closeNotificationCard}
          />
        </DrawerComponent>
      )}
      {query.has("notificationCard") &&
        query.get("notificationRole") !== "get" &&
        !currentSendNotification && (
          <SendNotificationDrawer listName={sendNotificationsListName} />
        )}
      {query.has("actionCardMobile") && (
        <ActionCardMobile
          open={true}
          setOpen={closeActionCardMobile}
          zIndex={140}
          actionID={query.get("actionCardMobile")}
        />
      )}
    </div>
  );
};

function getAreAllisSuccessed(employees: EmployeeItem[]) {
  return employees.every((cur) => cur.status === "success");
}

function sortEmployees(items: EmployeeEntry[]) {
  return items.toSorted((a, b) => {
    if (a[1].status === "success" && b[1].status === "pending") return -1;
    if (a[1].status === "pending" && b[1].status === "success") return 1;
    return 0;
  });
}

function SendNotificationCardMobile({
  item,
  onClose,
}: {
  item: TableBodySendNotificationItem;
  onClose: () => void;
}) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const questionsCount = useGetQuestionsCount(item.Test);

  const employees = sortEmployees([...item.Employees.entries()]);
  const isAllSuccess = getAreAllisSuccessed(
    employees.map(([, employee]) => employee),
  );
  const docs = [...item.Documents.values()];

  async function submitRetract() {
    setLoading(true);
    try {
      const deleteItems: {
        items: ListFieldsRecordType[typeof notificationsListName][];
      } = await getItemsRequest({
        listName: notificationsListName,
        filters: { NotificationID: item.NotificationID, Hide: false },
      });

      if (!deleteItems.items.length) return;

      const isAllUsersNotRead = deleteItems.items.every(
        (notification) => !notification.Read,
      );

      let updateIds: { ID: number; Hide?: boolean }[] = [];

      if (isAllUsersNotRead) {
        const deleteIds = deleteItems.items.map((notification) => ({
          ID: notification.ID,
        }));
        const chunks = chunkArray(deleteIds, 50);
        await Promise.all(
          chunks.map((chunk) =>
            deleteItemRequest({
              listName: notificationsListName,
              ID: 0,
              Ids: chunk,
            }),
          ),
        );
        updateIds = deleteIds;
      } else {
        updateIds = deleteItems.items.map((notification) => ({
          ID: notification.ID,
          Hide: true,
        }));
        const chunks = chunkArray(updateIds, 50);
        await Promise.all(
          chunks.map((chunk) =>
            updateItemRequest({
              listName: notificationsListName,
              body: {},
              bodyArr: chunk,
              update: false,
            }),
          ),
        );
      }

      const firstDoc = docs[0];
      if (firstDoc) {
        addAnalysis(
          currentUser,
          firstDoc.ID,
          firstDoc.Code,
          "isActive",
          "RetractNotification",
        );
      }

      if (updateIds.length > 0) {
        const { items } = await getItemsRequest({
          listName: emailQueueListName,
          filters: { NotificationID: item.NotificationID },
        });

        if (items.length > 0) {
          await deleteEmail(items.map((email) => ({ ID: email.ID })));
        }
      }

      dispatch(
        getNotificationsInfo({
          activeNotifyList: "send",
          listName: notificationsListName,
        }),
      );
    } catch (error) {
      console.error("submitRetract error", error);
    } finally {
      setLoading(false);
      setOpenDialog(false);
      onClose();
    }
  }

  return (
    <div className="h-full bg-[#F9FAFB] text-[#111827]">
      <header className="px-5 pt-5 pb-4 border-b border-[#D1D5DB]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-[16px] leading-8 font-medium text-[#111827]">
              {item.NotificationType.ID === notificationStatuses.OWD
                ? "Планирование OWD"
                : "Ознакомление"}
            </h2>
            <div className="text-[14px] leading-7 mt-2 text-[#637381]">
              <span className="inline-block w-3 h-3 bg-[#DFE4EA] rounded-full mr-2 align-middle" />
              До {item.ReviewDate.date}г.
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-[#111827]">
            <XMarkIcon className="w-9 h-9" />
          </button>
        </div>
      </header>

      <div className="px-5 py-6 space-y-6">
        <section className="pb-5 border-b border-[#D1D5DB]">
          <h3 className="text-[16px] leading-10 font-medium text-[#111827]">
            Документы: выбрано {docs.length}
          </h3>
          <div className="mt-4 space-y-3">
            {docs.map((document) => (
              <div
                key={document.ID}
                className="rounded-xl bg-[#F3F4F6] px-4 py-3"
              >
                <div className="text-[13px] leading-6 text-[#637381]">
                  {document.Code}
                </div>
                <div className="text-[13px] leading-6 text-darkblue mt-1 break-words">
                  {document.Title}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="pb-5 border-b border-[#D1D5DB]">
          <h3 className="text-[16px] leading-10 font-medium text-[#111827]">
            Сотрудники: выбрано {employees.length}
          </h3>
          <div className="mt-4 flex -space-x-1">
            {employees.slice(0, 4).map(([email, employee]) => (
              <UserAvatarItem
                key={email}
                item={{
                  email,
                  name: employee.name,
                  shortName: employee.shortName,
                  id: employee.id,
                }}
              />
            ))}
            {employees.length > 4 && (
              <div className="flex shrink-0 w-6 h-6 rounded-full ring-1 ring-white items-center justify-center bg-[#8899A8] text-white text-[10px]">
                +{employees.length - 4}
              </div>
            )}
          </div>
        </section>

        <section className="pb-5 border-b border-[#D1D5DB] text-[16px] leading-10 text-[#111827]">
          Тестирование:{" "}
          {item.NotificationType.ID === notificationStatuses.acquaintedTest ||
          item.NotificationType.ID === notificationStatuses.familiarization
            ? !!questionsCount
              ? `требуется, ${questionsCount} вопросов`
              : "не требуется"
            : "не требуется"}
        </section>

        <section className="space-y-3 pt-1">
          <TGButton
            disabled={isAllSuccess || loading}
            onClick={() => setOpenDialog(true)}
            className="w-full justify-center h-[46px] text-[14px]"
          >
            Отозвать уведомление
          </TGButton>
          <TGButton
            onClick={onClose}
            className="w-full justify-center h-[46px] text-[14px]"
          >
            Закрыть
          </TGButton>
        </section>
      </div>

      <Modal
        zIndex={110}
        open={openDialog}
        toggleDialog={setOpenDialog}
        modalClassName="!min-h-0 !min-w-0 !w-[calc(100vw-24px)] sm:!w-[600px] rounded-xl py-5"
      >
        <header className="px-5 pb-2">
          <h2>Подтвердите действие</h2>
        </header>

        <div className="px-5 py-4 grow">
          <p>Вы уверены, что хотите отозвать отправленное уведомление?</p>
        </div>

        <footer className="mt-2 flex flex-col gap-3 border-t px-5 pt-4">
          <TGButton
            disabled={loading}
            color="bluedark"
            onClick={submitRetract}
            className="h-[44px] w-full justify-center text-[14px]"
          >
            Да, отозвать
          </TGButton>
          <TGButton
            disabled={loading}
            onClick={() => setOpenDialog(false)}
            className="h-[44px] w-full justify-center text-[14px]"
          >
            Отмена
          </TGButton>
        </footer>
      </Modal>

      <Modal
        zIndex={120}
        modalClassName="min-h-0 min-w-min text-center"
        open={loading}
        toggleDialog={() => false}
        hideXMarkIcon={true}
      >
        <header className="px-10 pb-4">
          <h2>Идет сохранение данных</h2>
        </header>
        <div className="justify-center items-center px-10 space-y-5">
          <p className="text-lg">Пожалуйста подождите...</p>
          <Loading />
        </div>
      </Modal>
    </div>
  );
}

export default NotificationPageMobile;
